import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { legalPageService } from '@/modules/shared/services/legalPageService';
import { useAppTheme } from '@/modules/shared/contexts/AppThemeContext';
import { programUISettingsService } from '@/modules/programs/services/programUISettingsService';
import { AppPageSkeleton } from '@/modules/shared/components/ui/AppLoading';

export default function DynamicLegalPage() {
  const { pageKey } = useParams<{ pageKey: string }>();
  const { programId } = useParams<{ programId?: string }>();
  const { settings: appSettings } = useAppTheme();

  // Fetch the legal page content
  const { data: response, isLoading, isError } = useQuery({
    queryKey: ['legalPage', 'public', pageKey, programId],
    queryFn: () => legalPageService.getPublicPage(pageKey!, programId),
    enabled: !!pageKey,
  });

  // Fetch program UI settings for logo (only when programId is present)
  const { data: programSettingsRes } = useQuery({
    queryKey: ['programUISettings', programId],
    queryFn: () => programUISettingsService.getUISettings(programId!),
    enabled: !!programId,
  });

  const page = response?.data;
  const programSettings = programSettingsRes?.data;

  // If external URL, redirect
  useEffect(() => {
    if (page?.externalUrl) {
      window.location.href = page.externalUrl;
    }
  }, [page?.externalUrl]);

  // Determine logo URL: program logo → app logo fallback
  const logoUrl = programId
    ? (programSettings?.loginPageLogoUrl || appSettings?.loginPageLogoUrl || '')
    : (appSettings?.loginPageLogoUrl || '');

  if (isLoading || (page?.externalUrl)) {
    return (
      <div className="min-h-screen bg-background p-4">
        <AppPageSkeleton showFilters={false} showSearch={false} columns={2} rows={4} />
      </div>
    );
  }

  if (isError || !page) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center space-y-2">
          <h1 className="text-[16px] font-semibold text-foreground">Page Not Found</h1>
          <p className="text-[12px] text-muted-foreground">The requested legal page could not be found.</p>
        </div>
      </div>
    );
  }

  // Format last updated date
  const lastUpdated = page.lastUpdatedAt
    ? new Date(page.lastUpdatedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
    : null;

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Logo — top center */}
        {logoUrl && (
          <div className="flex justify-center mb-6">
            <img
              src={logoUrl}
              alt="Logo"
              className="max-h-12 w-auto object-contain"
            />
          </div>
        )}

        {/* Document content only — no title, no heading, no metadata */}
        <div
          className="prose prose-sm max-w-none text-[12px]"
          dangerouslySetInnerHTML={{ __html: page.contentHtml || '' }}
        />

        {/* Last Updated date at bottom */}
        {lastUpdated && (
          <p className="text-[11px] text-muted-foreground mt-8 text-center">
            Last Updated: {lastUpdated}
          </p>
        )}
      </div>
    </div>
  );
}
